import { ColumnDefinition } from "@/types/Table.type";

type Props = {
  columns: ColumnDefinition[];
  columnVisibility: Record<string, boolean>;
  setColumnVisibility: (columnVisibilities: Record<string, boolean>) => void;
  columnOrder: string[];
  setColumnOrder: (columnOrder: string[]) => void;
};

type UpdateColumnVisibilityProps = { column: ColumnDefinition } & Pick<
  Props,
  "columns" | "columnVisibility" | "setColumnVisibility"
>;

type UpdateColumnOrderProps = {
  column: ColumnDefinition;
  columnVisibility: Record<string, boolean>;
  index?: number;
} & Pick<Props, "columns" | "columnOrder" | "setColumnOrder">;

function updateColumnVisibility({
  column,
  columns,
  columnVisibility,
  setColumnVisibility,
}: UpdateColumnVisibilityProps) {
  const updatedColumnVisibilities: Record<string, boolean> = { ...columnVisibility };

  // Update the column itself
  updatedColumnVisibilities[column.id] = !columnVisibility[column.id];

  // Currently adding a new column or else removing a column
  if (!columnVisibility[column.id]) {
    // If the column is a dependent (has a group but is not the main column), ensure its main column is also visible
    if (column.group && !column.isMainColumn) {
      const mainColumn = columns.find((col) => col.group === column.group && col.isMainColumn);
      if (mainColumn) {
        updatedColumnVisibilities[mainColumn.id] = true;
      }
    }
  } else {
    // If a main column is hidden, remove all its dependent columns
    if (column.group && column.isMainColumn) {
      columns.filter((col) => col.group === column.group).forEach((col) => (updatedColumnVisibilities[col.id] = false));
    }
  }

  setColumnVisibility(updatedColumnVisibilities);
  return updatedColumnVisibilities;
}

function updateColumnOrder({
  column,
  columns,
  columnVisibility,
  columnOrder,
  setColumnOrder,
  index,
}: UpdateColumnOrderProps) {
  // Start with the current order, but only keep the columns that are visible.
  let updateColumnOrder = columnOrder.filter((id) => columnVisibility[id]);

  // Currently adding a new column or else removing a column
  if (columnVisibility[column.id]) {
    if (column.group && !column.isMainColumn) {
      // For a dependent column, ensure its main column is directly before it.
      const mainColumn = columns.find((col) => col.group === column.group && col.isMainColumn);
      if (mainColumn) {
        const mainIndex = updateColumnOrder.indexOf(mainColumn.id);
        if (mainIndex === -1) {
          // Main column is not in the order, so insert both as a block.
          const insertionIndex = index !== undefined ? index : updateColumnOrder.length;
          updateColumnOrder.splice(insertionIndex + 1, 0, mainColumn.id, column.id);
        } else {
          // Main column is present; remove any prior instance of the dependent column.
          updateColumnOrder = updateColumnOrder.filter((id) => id !== column.id);
          // Insert the dependent right after the main column.
          updateColumnOrder.splice(mainIndex + 1, 0, column.id);
        }
      } else {
        // Fallback: if no main column is found (should not occur), insert the dependent.
        const insertionIndex = index !== undefined ? index + 1 : updateColumnOrder.length;
        updateColumnOrder.splice(insertionIndex, 0, column.id);
      }
    } else {
      // For main columns or columns not in a group, insert at the given index.
      const insertionIndex = index !== undefined ? index + 1 : updateColumnOrder.length;
      if (!updateColumnOrder.includes(column.id)) {
        updateColumnOrder.splice(insertionIndex, 0, column.id);
      }
    }
  }

  setColumnOrder(updateColumnOrder);
  return updateColumnOrder;
}

export const useToggleColumnVisibility = ({
  columns,
  columnVisibility,
  setColumnVisibility,
  columnOrder,
  setColumnOrder,
}: Props) => {
  return (column: ColumnDefinition, index?: number) => {
    const updatedColumnVisibility = updateColumnVisibility({
      column,
      columns,
      columnVisibility,
      setColumnVisibility,
    });
    updateColumnOrder({ column, columns, columnVisibility: updatedColumnVisibility, columnOrder, setColumnOrder, index });
  };
};
